import React, { FC, useContext, useEffect, useState } from 'react';

import { Button, ClipboardButton, Link, RadioButton, TextInput, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { deleteDonationSettings, updateDonationSettings } from 'client/api';
import {
    defaultDonationSettings as DEFAULT_DONATION_SETTINGS,
    defaultSumValidator,
    walletNumberValidator,
} from 'client/blocks/DonationPage/DonationPage';
import { IconMedium } from 'client/blocks-redesign/AppIcons/AppIcons';
import { Field } from 'client/blocks-redesign/form/Field/Field';
import { PageLoader } from 'client/blocks-redesign/Loader/PageLoader';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { useFormSettings } from 'client/hooks/useFormSettings';
import { wrapInText } from 'client/lib/i18n-utils';
import { DonationSettings } from 'client/model/skill';
import * as commonKeyset from 'i18n/Common';
import * as keyset from 'i18n/Donation';
import * as generalKeyset from 'i18n/GeneralSettings';

import styles from './AliceAppDonationPage.module.scss';

const DONATION_SAVE = 'DONATION_SAVE';
const DONATION_DELETE = 'DONATION_DELETE';

const t = i18n(keyset);
const tCommon = i18n(commonKeyset);
const tGeneralSettings = i18n(generalKeyset);

const walletNumberDescription = wrapInText(
    (s) => (
        <Link href="https://yoomoney.ru/id" target="_blank" rel="noopener noreferrer">
            {s}
        </Link>
    ),
    (s) => (
        <Link href="https://yoomoney.ru/reg" target="_blank" rel="noopener noreferrer">
            {s}
        </Link>
    ),
)(t('donation-wallet-description'));

const defaultSumDescription = wrapInText((s) => (
    <Link href="https://yoomoney.ru/page?id=523014" target="_blank" rel="noopener noreferrer">
        {s}
    </Link>
))(t('donation-sum-hint-description'));

const getStubText = (hasDonationSettings: boolean, hideInStore: boolean) => {
    if (hideInStore) {
        return t('stub-text-hide-store');
    }

    if (!hasDonationSettings) {
        return t('stub-text-has-donat');
    }

    return t('stub-text');
};

interface Props {
    disabled: boolean;
}

export const AliceAppDonationPage: FC<Props> = ({ disabled }) => {
    const [displayDonationForm, setDisplayDonationForm] = useState(false);
    const context = useContext(CurrentAliceAppContext);
    const { add: addToast } = useToaster();

    useEffect(() => {
        if (context?.aliceApp) {
            setDisplayDonationForm(!!context.aliceApp?.donationSettings);
        }
    }, [context?.aliceApp]);

    const { settings, onChange, errors, hasErrors, setSettings } = useFormSettings<DonationSettings>({
        defaultSettings: context?.aliceApp?.donationSettings || DEFAULT_DONATION_SETTINGS,
        validators: {
            defaultSum: defaultSumValidator,
            walletNumber: walletNumberValidator,
        },
    });

    const [updateSettings, savingState] = useFetchingHandler({
        handler: async () => {
            if (!context?.aliceApp) return;
            await updateDonationSettings(context.aliceApp.id, settings);
            await context.refreshAliceApp();
        },
        onError: () => addToast({ title: tGeneralSettings('error'), theme: 'danger', name: DONATION_SAVE }),
        onLoad: () => addToast({ title: tCommon('saved'), theme: 'success', name: DONATION_SAVE }),
    });

    const [deleteSettings, deletingState] = useFetchingHandler({
        handler: async () => {
            if (!context?.aliceApp) return;
            await deleteDonationSettings(context.aliceApp.id);
            await context.refreshAliceApp();
            setSettings(DEFAULT_DONATION_SETTINGS);
        },
        onError: () => addToast({ title: tGeneralSettings('error'), theme: 'danger', name: DONATION_DELETE }),
        onLoad: () => addToast({ title: t('donation-disabled'), theme: 'success', name: DONATION_DELETE }),
    });

    if (!context?.aliceApp) {
        return <PageLoader />;
    }

    const skill = context.aliceApp;
    const { defaultSum, walletNumber } = settings;
    const isSettingsValid = !hasErrors && Boolean(defaultSum) && Boolean(walletNumber);
    const isSaving = savingState === 'loading';
    const isDeleting = deletingState === 'loading';

    if (!skill.onAir) {
        return (
            <>
                <header className={styles.DonationPage__header}>
                    <div className={styles.DonationPage__header__titleBlock}>
                        <IconMedium imageUrl={skill.logo2?.url} />
                        <h1 className={styles.DonationPage__title}>{skill.draft?.name}</h1>
                    </div>
                </header>
                <section className={styles.DonationPage__content}>
                    <div className={styles.DonationPage__stubContainer}>{t('error-no-published')}</div>
                </section>
            </>
        );
    }

    const donationFormLink = `${__appConfig.app.storeUrl}/skills/${skill.slug}?action=donation`;

    return (
        <>
            <header className={styles.DonationPage__header}>
                <div className={styles.DonationPage__header__titleBlock}>
                    <IconMedium imageUrl={skill.logo2?.url} />
                    <h1 className={styles.DonationPage__title}>{skill.draft?.name}</h1>
                </div>
                <div className={styles.DonationPage__buttons}>
                    <Button
                        className={styles.DonationPage__saveButton}
                        onClick={updateSettings}
                        view="action"
                        loading={isSaving}
                        disabled={disabled || !isSettingsValid || isDeleting}
                    >
                        {t('button-save')}
                    </Button>
                    {Boolean(skill.donationSettings) && (
                        <Button
                            onClick={deleteSettings}
                            view="normal"
                            loading={isDeleting}
                            disabled={disabled || isSaving}
                        >
                            {t('button-off')}
                        </Button>
                    )}
                </div>
            </header>
            <section className={styles.DonationPage__content}>
                <h2 className={styles.DonationPage__pageTitle}>{t('page-header-title')}</h2>
                <Field fieldTitle={t('form-settings')} fieldDescription={t('donation-form-toggler-description')}>
                    <RadioButton
                        className={styles.DonationPage__formElement}
                        options={[
                            { value: 'True', content: t('donation-enabled') },
                            { value: 'False', content: t('donation-disabled') },
                        ]}
                        value={displayDonationForm ? 'True' : 'False'}
                        onUpdate={(value) => {
                            if (value === 'False') deleteSettings();
                            setDisplayDonationForm(value === 'True');
                        }}
                        size="xl"
                        disabled={disabled}
                    />
                </Field>
                {displayDonationForm && (
                    <>
                        <Field
                            fieldTitle={t('label-wallet')}
                            required
                            error={errors.walletNumber}
                            fieldDescription={walletNumberDescription}
                        >
                            <TextInput
                                className={styles.DonationPage__formRow}
                                value={walletNumber}
                                onChange={(e) => onChange('walletNumber')(e.target.value)}
                                placeholder={t('donation-enter-number')}
                                size="xl"
                                disabled={disabled}
                            />
                        </Field>

                        <Field
                            fieldTitle={t('label-sum')}
                            error={errors.defaultSum}
                            required
                            fieldDescription={defaultSumDescription}
                        >
                            <TextInput
                                className={styles.DonationPage__formRow}
                                value={defaultSum}
                                onChange={(e) => onChange('defaultSum')(e.target.value)}
                                size="xl"
                                disabled={disabled}
                                endContent={
                                    <div
                                        className={styles.DonationPage__currency}
                                        style={{
                                            width:
                                                document.documentElement.clientWidth > 640
                                                    ? `${520 - (defaultSum?.length || 0) * 9}px`
                                                    : '28px',
                                        }}
                                    >
                                        {'₽'}
                                    </div>
                                }
                            />
                        </Field>

                        {Boolean(skill.donationSettings) && !skill.hideInStore && (
                            <Field fieldTitle={t('additional-link')}>
                                <div className={styles.DonationPage__copyField}>
                                    <TextInput
                                        value={donationFormLink}
                                        size="xl"
                                        endContent={
                                            <ClipboardButton text={donationFormLink} size="l" view="flat-secondary" />
                                        }
                                        readOnly
                                    />
                                </div>
                            </Field>
                        )}

                        {(skill.hideInStore || !skill.donationSettings) && (
                            <Field
                                fieldTitle={t('additional')}
                                fieldDescription={getStubText(Boolean(skill.donationSettings), skill.hideInStore)}
                            />
                        )}
                    </>
                )}
            </section>
        </>
    );
};
